import { useCallback, useEffect, useState } from "react";
import {
  persistMuted,
  playGameSound,
  readStoredMuted,
  unlockGameAudio,
  type GameSoundName,
} from "./game-sounds";

/** Return value of {@link useGameSounds}. */
export type GameSoundsApi = {
  /** Play one SFX. No-op while muted or before the audio is unlocked. */
  play: (name: GameSoundName) => void;
  /** Whether SFX are currently muted (persisted in `localStorage`). */
  muted: boolean;
  /** Flip and persist the mute preference. */
  toggleMute: () => void;
};

function getStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * React hook exposing the game's SFX layer: `play(name)`, the `muted` flag, and
 * `toggleMute()`. Mute is initialised from and persisted to `localStorage`, so
 * the preference survives reloads and is shared across every instance/tab.
 *
 * The shared `AudioContext` starts suspended (browser autoplay policy); this
 * hook installs one-time, capture-phase gesture listeners that unlock it on the
 * first interaction anywhere in the tab, so subsequent `play` calls are audible.
 */
export function useGameSounds(): GameSoundsApi {
  const [muted, setMuted] = useState<boolean>(() => readStoredMuted(getStorage()));

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let unlocked = false;
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];

    const handleGesture = () => {
      if (unlocked) {
        return;
      }
      unlocked = true;
      unlockGameAudio();
      for (const eventName of events) {
        window.removeEventListener(eventName, handleGesture, true);
      }
    };

    for (const eventName of events) {
      window.addEventListener(eventName, handleGesture, true);
    }

    return () => {
      for (const eventName of events) {
        window.removeEventListener(eventName, handleGesture, true);
      }
    };
  }, []);

  const play = useCallback(
    (name: GameSoundName) => {
      if (muted) {
        return;
      }
      playGameSound(name);
    },
    [muted],
  );

  const toggleMute = useCallback(() => {
    setMuted((previous) => {
      const next = !previous;
      persistMuted(getStorage(), next);
      return next;
    });
  }, []);

  return { play, muted, toggleMute };
}
